import {useDispatch} from 'react-redux';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  AuditDataAssetsV2Body,
  InspectionType,
  useAuditAssetsUploadV2Mutation,
  useLazyLoadAuditDataByVehicleIdQuery,
  useUpdateAuditConditionDataMutation,
} from '@dms/api/core';
import {handleApiError} from '@dms/shared';
import {clearAuditAssets, clearAuditFields, getUpdateRequestBodyFromFormData} from '@dms/teas';

export const useConditionDataSubmit = () => {
  const dispatch = useDispatch();
  const [loadAuditDataByVehicleId] = useLazyLoadAuditDataByVehicleIdQuery();
  const [updateAuditConditionData] = useUpdateAuditConditionDataMutation();
  const [auditAssetsUploadV2] = useAuditAssetsUploadV2Mutation();

  const handleSubmit = async (
    vehicleId: string,
    conditionFormValues: Record<string, unknown>,
    conditionAssets: Record<string, AuditDataAssetsV2Body>
  ): Promise<void> => {
    if (!vehicleId) {
      throw new Error('Vehicle not found');
    }

    const {data: initialConditionData} = await loadAuditDataByVehicleId({
      inspectionType: InspectionType.VEHICLE_CONDITION,
      body: {
        vehicleIds: [vehicleId],
      },
    });

    if (!initialConditionData?.length) {
      return;
    }

    const condition = initialConditionData.at(0);

    if (!condition?.id) {
      throw new Error('Invalid condition data');
    }

    if (!isNilOrEmpty(conditionFormValues)) {
      await updateAuditConditionData({
        auditId: condition.id,
        requestBody: getUpdateRequestBodyFromFormData(conditionFormValues),
      })
        .unwrap()
        .then(() => dispatch(clearAuditFields()))
        .catch(handleApiError);
    }

    if (isNotNil(condition?.id) && !isNilOrEmpty(conditionAssets)) {
      await auditAssetsUploadV2({
        requestBody: {
          auditId: condition.id,
          assets: Object.values(conditionAssets),
        },
      })
        .unwrap()
        .then(() => dispatch(clearAuditAssets()))
        .catch(handleApiError);
    }
  };

  return {
    handleSubmit,
  };
};
